import { formatDate, formatNumber, formatRelativeDate, getLocaleSnapshot, translate } from '@/lib/i18n/runtime';
import type { TeamChatLiveAlert } from './live-alert-store';

export type AlertPreviewCopy = {
  kind: 'audio' | 'image' | 'video' | 'file' | 'system' | 'message' | 'pending' | 'thread_assigned' | 'thread_due';
  title?:string;
  dueAt?:string;
  byName?:string;
  fileName?: string;
};

export const commsText = (key: string, params = {}) => translate('comms.' + key, params, 'common');
export const cappedCount = (count: number) => formatNumber(Math.min(count, 99)) + (count > 99 ? '+' : '');

/** Truncate previews by code point without splitting UTF-16 surrogate pairs. */
export function alertPreview(value: string, limit = 180): string {
  let out = '', count = 0;
  for (const point of value) {
    if (count === limit) return out + '…';
    out += point;
    count++;
  }
  return out;
}

export function previewProductCopy(copy: AlertPreviewCopy): string {
  if(copy.kind==='thread_assigned'||copy.kind==='thread_due'){
    const title=translate('notifications.'+(copy.kind==='thread_assigned'?'threadAssigned':'threadDue'),{title:copy.title||translate('notifications.threadTitle',{},'common')},'common');
    const details=copy.kind==='thread_assigned'?copy.byName?translate('notifications.threadBy',{name:copy.byName},'common'):'' :copy.dueAt?formatRelativeDate(copy.dueAt):'';
    return title+(details?' · '+details:'');
  }
  if (copy.kind === 'file' && copy.fileName) return commsText('preview.fileNamed', { name: copy.fileName });
  return commsText('preview.' + copy.kind);
}

export function messageAlertCopy(data: Record<string, unknown> | undefined): {
  preview: string;
  previewCopy?: AlertPreviewCopy;
} {
  const content = typeof data?.content === 'string' ? data.content : '';
  if (content) return { preview: alertPreview(content) };
  const type = typeof data?.message_type === 'string' ? data.message_type : '';
  const fileName = typeof data?.file_name === 'string' ? data.file_name : '';
  const kind = ['audio', 'image', 'video', 'file', 'system'].includes(type)
    ? type as AlertPreviewCopy['kind']
    : 'message';
  return { preview: '', previewCopy: { kind, ...(fileName ? { fileName } : {}) } };
}

export function alertDisplayCopy(alert: Pick<TeamChatLiveAlert,
  'senderName' | 'channelName' | 'isDirect' | 'isMention' | 'preview' | 'previewCopy'
>) {
  const sender = alert.senderName || commsText('teamMessage');
  return {
    sender,
    channel: alert.channelName || commsText(alert.isDirect ? 'direct' : 'internal'),
    preview: alert.previewCopy ? previewProductCopy(alert.previewCopy) : alert.preview,
    title: alert.isMention ? translate('notifications.mentionTitle', { name: sender }, 'common') : sender,
  };
}

export function formatCommsTime(value?: string): string | undefined {
  if (!value || !Number.isFinite(Date.parse(value))) return undefined;
  const delta = Date.parse(value) - Date.now(), minutes = Math.floor(Math.abs(delta) / 60_000);
  if (minutes < 1) return commsText('now');
  if (minutes < 48 * 60) return formatRelativeDate(value);
  return formatDate(value, { day: '2-digit', month: 'short' }, getLocaleSnapshot().locale);
}
